<script lang="ts" setup>
import { FormKitLazyProvider } from '@formkit/vue'
import { useElementHover } from "@vueuse/core"
import type { InputTextProps } from "./InputText.props"
import { ValidationVisiblityEnum } from "~~/types/validation"
import { debounce } from "lodash"
import type { ITransformRule } from "~/utils/input/transformer/transformer.model"
import {
  ERuleName,
  ETrasformType
} from "~/utils/input/transformers/transformers.model"
import { transformers } from "~/utils/input/transformers/transformers"

const props = withDefaults(defineProps<InputTextProps>(), {
  type: "text",
  placeholder: "",
  disabled: false,
  helpText: "",
  validation: "",
  forceError: false,
  showRequiredness: false,
  showValidationMessagesAlways: false,
  validationMessages: undefined,
  isDropdown: false,
  isCalendarSelector: false,
  hasFixedWidth: false,
  showAsterisk: false,
  validationVisibility: ValidationVisiblityEnum.Blur,
  debounceTime: 0,
  forceEmitBeforeDebounce: false,
  transformRules: []
})

const emit =
  defineEmits<{
    (e: "update:modelValue", value: string): string
    (e: "onUpdateValidity", value: boolean): void
    (e: "onUpdateDirty", value: boolean): void
    (e: "onFocusIn"): void
    (e: "onFocusOut"): void
    (e: "onEdit"): void
  }>()

const inputField = ref("")
const inputType = ref(props.type)
const inputElement = ref()
const container = ref()
const isInputDisabled = ref(props.disabled)
const isFocused = ref(false)
const maskError = ref()
const forceError = toRef(props.forceError)

//AUTOFILL HANDLERS
const isAutocompleted = ref(false)

onMounted(() => {
  inputElement.value = container.value?.querySelector("input")
  if (inputElement.value) {
    inputElement.value.addEventListener("animationstart", () => {
      isAutocompleted.value = true
    })
  }
})

onUnmounted(() => {
  inputElement.value = container.value?.querySelector("input")
  if (inputElement.value) {
    inputElement.value.removeEventListener("animationstart", () => {
      isAutocompleted.value = true
    })
  }
})

//USER AGENT SNIFFING
const isFirefox = ref(false)

onMounted(() => {
  const userAgent = navigator.userAgent.toLowerCase()
  isFirefox.value = userAgent.includes("firefox")
})

const applyTransformationRule = (
  value: string | number,
  rule: ITransformRule
) => {
  switch (rule.type) {
    case ETrasformType.replace:
      return transformers.replaceStringTransformer(
        value,
        rule.match,
        rule.replacer
      )
    case ETrasformType.toString:
      return value.toString()
    default:
      return value
  }
}

const trasformEmittableValue = (
  value: string | number,
  transformRules: ITransformRule[]
) =>
  transformRules.reduce(
    (transformedValue, rule) => applyTransformationRule(transformedValue, rule),
    value
  )

const setDefaultTransformRules = (): ITransformRule[] =>
  transformers.getTransformesRules([ERuleName.toString])

const emitModel = (value: string | number) => {
  const rules = setDefaultTransformRules().concat(props.transformRules)
  const emittableValue = trasformEmittableValue(value, rules)
  typeof emittableValue === "string" &&
    emit("update:modelValue", emittableValue)
}

const debounceFunction = debounce((value) => {
  emitModel(value)
}, props.debounceTime)

//MODEL VALUE
const value = computed({
  get() {
    return props.modelValue
  },
  set(value) {
    if (!value) {
      value = ""
    }
    //Emit full value before debounce
    if (props.forceEmitBeforeDebounce) {
      emit("update:modelValue", value.toString())
    }
    debounceFunction(value)
  }
})

//VALIDITY HANDLERS
const isValid = computed(() => {
  const node = inputField.value?.node
  const completed = node?.context?.state?.complete

  if (props.maskaFormat && completed) {
    forceError.value = false
    maskError.value = ""
  }

  // As per TF-2493, the fiscal code input should not show the error only when the input loses focus
  if (
    props.avoidInitialErrorValidation && // This is the flag to activate this behaviour
    !completed && // If the value is not correct (e.g the fiscal code is not right yet)
    isFocused.value // If the input is being focused
  ) {
    return true
  }

  if (forceError.value) {
    return false
  }

  if (inputField.value) {
    if (
      (node.context.state.dirty || node.context.state.blurred) &&
      node.context.state.rules
    ) {
      return completed
    }
  }

  return true
})

watch(isValid, (newValue) => {
  emit("onUpdateValidity", newValue)
})

//DIRTY HANDLERS
const isDirty = computed(() => {
  if (inputField.value?.node) {
    const node = inputField.value.node
    return node.context.state.dirty
  }
  return false
})

watch(isDirty, (newValue) => {
  emit("onUpdateDirty", newValue)
})

//FOCUS HANDLERS

const handleFocusIn = () => {
  if (isFocused.value) return
  isFocused.value = true
  emit("onFocusIn")
}

const handleFocusOut = () => {
  if (!isFocused.value) return
  isFocused.value = false
  emit("onFocusOut")

  // For the TF-2540, we are manually setting the error message for the "maskaFormat" prop since Formkit doens't do it by itself
  if (props.maskaFormat) {
    const node = inputField.value?.node
    const parsedRules = node?.props?.parsedRules
    const currentRule = parsedRules?.find(
      (rule: any) =>
        rule?.name !== "required" && rule?.messageObserver && !rule?.state
    )
    const currentErrorMessage =
      props.validationMessages[!value?.value ? "required" : currentRule?.name]

    maskError.value = currentErrorMessage

    if (!value.value) forceError.value = true
  }
}

const focusInput = () => {
  const myElement = document.getElementById(props.name)
  if (myElement) {
    myElement.focus()
    emit("onFocusIn")
  }
}

const labelFocusBehaviour = computed(() => {
  if (props.inlineLabel) {
    // with an inlineLabel, the animation never displays
    return false
  } else if (props.forceFocus) {
    return true
  } else if (props.isDropdown) {
    // if this is input is dropdown, the label should rise only when there is a value, and not on focus
    return !!value.value || !!isAutocompleted.value
  } else {
    // regular behaviour: the label rises if the input is focused or contains a value (including autocomplete)

    return (
      !!value.value ||
      isFocused.value ||
      isAutocompleted.value ||
      !!inputField.value?.node?.props?._maskValue // When "mask"ed inputs, the "value.value" doesn't get populated until the end of the "maska-format" .. staying empty, so thats why we use this "_maskValue" which has the actual current value, so we also gotta check this one
    )
  }
})

const labelClass = computed(() => {
  return {
    "floating-label__textfield !top-0 -translate-y-1/2 bg-grey-main px-1 ":
      labelFocusBehaviour.value
  }
})

//VALIDATION MESSAGES
const showValidationMessages = props.showValidationMessagesAlways
  ? !!props.validationMessages
  : props.type === "password" || props.type === "email"

//REQUIREDNESS HANDLER
const isRequired = computed(() => {
  return (
    props.isRequired ||
    (props.validation &&
      typeof props.validation === "string" &&
      props.validation.includes("required")) ||
    (Array.isArray(props.validation) &&
      props.validation.some((i) => i.includes("required")))
  )
})

//MAX LENGTH HANDLERS
const maxChar = computed(() => {
  if (props.validation && props.validation.toString().includes("length:")) {
    const lengthVal = props.validation
      .toString()
      .match(/length:\d{1,3}\,\d{1,3}/)

    return lengthVal?.length ? lengthVal[0].split(",")[1] : 0
  }
  return 0
})

const currentLength = computed(() => {
  return typeof value.value == "string" ? value.value?.length : 0
})

const countChars = computed(() => {
  return `${currentLength.value}/${maxChar.value} `
})

//PASSWORD ICON & VISIBILITY HANDLERS
const showPassword = computed(() => (props.type === "password" ? true : false))

const isPasswordHidden = ref(false)
const suffixIconsNotAbsolute = props.type == "password" || props.type == "email"

const togglePassword = () => {
  inputType.value = isPasswordHidden.value ? "password" : "text"
  isPasswordHidden.value = !isPasswordHidden.value
}

//EMAIL HANDLERS
const handleEdit = () => {
  emit("onEdit")
}

//TOOLTIP HANDLERS
const questionMark = ref()
const isQuestionMarkOnHover = ref(
  import.meta.server ? false : useElementHover(questionMark)
)

const isTooltipVisible = ref(false)
watch(isQuestionMarkOnHover, (newValue) => {
  newValue ? (isTooltipVisible.value = true) : (isTooltipVisible.value = false)
})
</script>

<template>
  <div ref="container" class="input-text-component">
    
<FormKitLazyProvider config-file="true">
<FormKit
      ref="inputField"
      :id="id ?? name"
      :name="name"
      v-model="value"
      :type="inputType"
      :autocomplete="autocomplete"
      :placeholder="(isFirefox ? '' : label) || placeholder"
      :validation="validation"
      :disabled="isInputDisabled"
      :validation-rules="validationRules"
      :mask="maskaFormat"
      :show-mask="false"
      :validation-messages="validationMessages"
      :validation-visibility="props?.validationVisibility"
      :readonly="isReadonly"
      inner-class="h-10"
      :classes="{
        outer: {
          relative: true,
          'pt-[6px]': !!label || !!customDropdownLabel
        },
        wrapper: 'relative bg-inherit',
        inner: {
          //!NOTE: with suffixIconsNotAbsolute == true, theformkit-input border is disabled, so all the border properties have to be transferred to formkit-inner
          'flex flex-row border !outline-none rounded border-black-10 hover:border-slate-800 focus:border-green-70':
            suffixIconsNotAbsolute,
          'border-negative-main': suffixIconsNotAbsolute && !isValid,
          'border-grey-main bg-grey-main': disabled
        },
        input: {
          'disabled:text-black-40 py-2 pl-4 pr-3 w-full input-text__textfield placeholder:sr-only bg-white !outline-none': true,
          'error-border__textfield': !isValid && !suffixIconsNotAbsolute,
          'caret-transparent cursor-pointer pr-4 text-font-bold': isDropdown,
          'self-center justify-self-start rounded !ring-0  !border-0 !border-none !hover:border-none !focus:border-none !disabled:border-none ![dirty]:border-none !focus-visible:outline-none':
            suffixIconsNotAbsolute,
          '[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none':
            type === 'number',
          [inputClass as any]: inputClass
        },
        message: 'text-negative-main mouse mt-[3px]',
        messages: {
          hidden: !showValidationMessages || !validationMessages,
          inline:
            showValidationMessages &&
            !!validationMessages &&
            (!helpText || isHelpTextHidden || isValid),
          'block':
            showValidationMessages &&
            !!validationMessages &&
            !!helpText &&
            !isHelpTextHidden &&
            !isValid
        }
      }"
      @focusin="handleFocusIn"
      @focusout="handleFocusOut"
      class="text_fields-input_text"
    >
      <template #prefixIcon>
        <div class="absolute grid h-full w-fit place-content-center">
          <slot name="prefixIcon" />
        </div>
      </template>

      <!-- <template v-if="maskaFormat" #input>
        <input
          :id="id ?? name"
          :name="name"
          ref="target"
          v-model="value"
          v-maska
          :autocomplete="autocomplete"
          :disabled="isInputDisabled"
          :data-maska="maskaFormat"
          :class="{
            'input-text__textfield bg-white disabled:text-black-40 w-full !appearance-none py-2 pl-4 pr-3 !outline-none placeholder:sr-only': true,
            'error-border__textfield': !isValid,
            'text-font-bold caret-transparent cursor-pointer pr-4': isDropdown,
            '[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none':
              type === 'number'
          }"
          type="text"
          @focusin="handleFocusIn"
          @focusout="handleFocusOut"
        />
        <p class="mouse text-negative-main pt-1">{{ maskError }}</p>
      </template> -->

      <template #label>
        <label
          :for="name"
          class="
            label__textfield
            pointer-events-none
            absolute
            left-4
            top-1/2
            z-[2]
            flex
            -translate-y-1/2
            gap-1
            transition-all
          "
          :class="{
            ...labelClass,
            'bg-white': inlineLabel || !isInputDisabled,
            'bg-grey-main': isInputDisabled,
            '!text-negative-main': !isValid,
            'top-5 cursor-pointer select-none': isDropdown,
            'font-bold': isLabelTextBold
          }"
          @click="focusInput"
        >
          <span v-if="customDropdownLabel" class="bg-white font-normal">
            {{ customDropdownLabel }}
          </span>
          {{ label }} <b v-if="inlineLabel" class="font-normal">{{ value }}</b>
          <!-- <IconsMandatory v-if="isRequired" /> -->
          <span v-if="!isRequired && showRequiredness">{{
            $t("general.optionalField")
          }}</span>
          <p v-if="showAsterisk && isRequired">*</p>
        </label>
      </template>

      <template #suffixIcon="{ state }">
        <!-- ALTERNATIVE ICON TEMPLATE for password and email fields
            It gives them relative positioning rather than the default absolute
        This avoids overlapping between browser password managers and formkit's suffixicons -->
        <div
          v-if="suffixIconsNotAbsolute"
          class="
            alternative-suffix-icons-template
            bg-transparent
            relative
            flex
            items-center
            self-center
            justify-self-end
            rounded-lg
          "
        >
          <IconsCheckmark
            v-if="
              typeof showCheckmark === 'boolean'
                ? showCheckmark
                : !disabled && !!value && state.complete && state.rules
            "
            class="text-green-500 order-2 mr-3 h-6 w-6"
          />
          <div
            v-if="!disabled && showPassword"
            class="
              text-green-main
              order-1
              mr-1
              cursor-pointer
              select-none
              rounded-r-lg
              p-2
            "
            @click="togglePassword"
          >
            <IconsShowPasswordNoText
              v-if="value && showPassword && !isPasswordHidden"
            />
            <IconsHidePasswordNoText v-if="value && isPasswordHidden" />
          </div>
          <IconsEdit
            v-if="!showPassword && isInputDisabled"
            class="text-green-500 order-2 mr-3 h-6 w-6"
            @click="handleEdit"
          />
        </div>
        <!-- DEFAULT ICON TEMPLATE-->
        <div class="default-suffix-icon-template" v-else>
          <IconsChevronDown
            v-if="isDropdown && !isCalendarSelector"
            :class="{ hidden: isFocused, 'cursor-pointer': isDropdown }"
            class="
              pointer-events-none
              absolute
              right-1
              top-1/2
              h-6
              w-6
              -translate-y-1/2
            "
          />
          <IconsCalendar
            @click="handleFocusIn"
            v-else-if="isCalendarSelector && !hasCalendarIconHidden"
            class="
              absolute
              right-3
              top-1/2
              -z-0
              h-6
              w-6
              -translate-y-1/2
              cursor-pointer
            "
          />
          <IconsCheckmark
            v-if="
              typeof showCheckmark === 'boolean'
                ? showCheckmark
                : !disabled && !!value && state.complete && state.rules
            "
            class="
              text-green-500
              absolute
              right-3
              top-1/2
              h-6
              w-6
              -translate-y-1/2
            "
          />
          <IconsQuestionMarkCircle
            v-if="showQuestionMark"
            ref="questionMark"
            class="
              text-gray-500
              absolute
              right-1
              top-1/2
              h-6
              w-6
              -translate-y-1/2
            "
          />
          <UITooltip
            v-if="isTooltipVisible"
            class="c_lg:top-12 absolute top-10"
          />
          <div
            v-if="!disabled"
            class="
              text-green-main
              absolute
              right-[1px]
              top-0
              cursor-pointer
              rounded-r-lg
            "
          >
            <IconsShowPassword
              v-if="value && showPassword && !isPasswordHidden"
              @click="togglePassword"
            />
            <IconsHidePassword
              v-if="value && isPasswordHidden"
              @click="togglePassword"
            />
          </div>

          <div
            class="
              absolute
              right-0
              top-0
              grid
              h-full
              w-fit
              place-content-center
            "
          >
            <slot name="suffixIcon" />
          </div>
        </div>
      </template>
      <template #help>
        <div
          class="
            help-text__textfield
            mouse
            absolute
            left-0
            right-0
            flex
            justify-between
            pt-1
          "
          :class="[
            !!label || !!customDropdownLabel ? 'top-[46px]' : 'top-10',
            !isValid ? 'pt-6' : ''
          ]"
        >
          <!-- Default help text fallback: displays the helpText prop when no custom slot content is provided.
          Only shows when the input is invalid or when help text is not explicitly hidden. -->
          <slot name="helpText">
            <p v-if="!isValid || !isHelpTextHidden" class="text-black-80">
              {{ helpText }}
            </p>
          </slot>
          <p v-if="maxChar && !isCountCharHidden" class="mr-4">
            {{ countChars }}
          </p>
        </div>
      </template>
    </FormKit>
</FormKitLazyProvider>

  </div>
</template>

<style lang="scss">

input:-webkit-autofill {
  animation-name: onAutoFillStart;
  transition: background-color 50000s ease-in-out 0s;
}

@keyframes onAutoFillStart {
  from {
    /**/
  }
  to {
    /**/
  }
}

</style>
